// Alex and Chad's side of the scope gate (Oct 30, 2026), in the install
// agreement review: why the deal doesn't match the carrier's scope, the RM's
// override request, the scope files and the lines left with the homeowner, and
// the three ways forward: use the carrier's numbers and approve, override with
// a reason and approve, or send it back to the RM. Trigger deals_scope_gate
// refuses an approval the check blocks unless it carries Alex's or Chad's
// override with a reason (5+ characters).

import { useState } from "react";
import {
  carrierNumbersFixAll,
  dealPicks,
  fmtScopeMoney as money,
  openScopeFile,
  type ScopeGateStatus,
} from "../../lib/scopeGate";
import type { Deal } from "../../lib/types";
import { PickedLinesList } from "./ScopeCheckCard";

function when(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
}

export default function ScopeGateApproverPanel({
  deal,
  status,
  rmName,
  canOverride,
  busy,
  onApproveWithCarrierNumbers,
  onOverride,
  onSendBack,
  showPicks = true,
}: {
  deal: Deal;
  status: ScopeGateStatus;
  rmName: string | null;
  /** The review window lists the lines in its own summary already. */
  showPicks?: boolean;
  /** Alex, Chad, admin (super users). Finance can approve agreements but not override the check. */
  canOverride: boolean;
  busy: boolean;
  onApproveWithCarrierNumbers: () => void | Promise<void>;
  onOverride: (reason: string) => void | Promise<void>;
  onSendBack: (note: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [fileErr, setFileErr] = useState<string | null>(null);
  const blocks = status.applies && status.blocks;
  // The check says whether it can be overridden at all: never with no carrier scope on the deal, never while the
  // scope is being read (the RM's request follows the same rule). Matched here so the panel never offers an
  // override the check doesn't (second review, Oct 30, 2026).
  const overridable = status.can_override;
  const request = status.gate?.request;
  const off = status.fields.filter((f) => f.match === false);
  const picks = dealPicks(deal);
  const creditsOk = !status.credits || status.credits.ok;
  const numbersFix = blocks && status.state === "mismatch" && creditsOk && carrierNumbersFixAll(status.fields);
  const reasonOk = reason.trim().length >= 5;
  const rmFirst = (rmName ?? "").trim().split(/\s+/)[0] || "the RM";

  // Alex or Chad's words to the RM (the check's own message is written for the RM, "ask Alex or Chad").
  const stateLine: Record<string, string> = {
    no_scope: "There's no carrier scope on this deal: upload it.",
    reading: "The carrier's scope is still being read.",
    not_read: "The carrier's scope hasn't been read yet.",
    unreadable: "The CRM couldn't read the carrier's scope: upload the full scope.",
    flagged: "The read of the carrier's scope didn't check out, so its numbers need a look.",
  };
  const creditsLine: Record<string, string> = {
    typed: "The credits were typed in: pick the lines Bardin won't do from the carrier's scope.",
    stale: "Some lines left with the homeowner aren't on the carrier's current scope: pick the lines again.",
    mismatch: "The credits don't add up to the lines picked: save the picks again.",
    no_lines: "The carrier's scope has no line items to pick the credits from.",
  };
  const sendBackNote = [
    off.length > 0
      ? `The deal doesn't match the carrier's scope. On the scope: ${off.map((f) => `${f.label} ${f.carrier == null || (f.key === "pwi_code" && !f.carrier) ? "none" : money(f.carrier)}`).join(", ")}.`
      : stateLine[status.state] ?? null,
    !creditsOk && status.credits ? creditsLine[status.credits.state] ?? null : null,
    "Fix it on the scope check and resubmit.",
  ].filter(Boolean).join(" ");

  return (
    <div className={`rounded-xl border p-4 ${blocks ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`}>
      <div className={`text-xs font-semibold uppercase tracking-wider ${blocks ? "text-rose-800" : "text-amber-800"}`}>
        {blocks ? (overridable ? "Needs an override" : "Can't approve yet") : "The RM asked for an override"}
      </div>

      {request && (
        <div className="mt-2 rounded-lg bg-white border border-slate-200 p-2.5 text-sm">
          <div className="text-[11px] text-slate-500">
            {request.by_name ?? "The RM"} asked{request.at ? ` · ${when(request.at)}` : ""}
          </div>
          <div className="text-bardin-ink">{request.note ? `“${request.note}”` : "(no note)"}</div>
        </div>
      )}

      {blocks ? (
        <div className="mt-2 text-sm text-rose-900">{status.message ?? "The deal doesn't match the carrier's scope."}</div>
      ) : (
        <div className="mt-2 text-sm text-amber-900">The deal now matches the carrier's scope, so it can be approved as it is.</div>
      )}

      {off.length > 0 && (
        <div className="mt-2 overflow-hidden rounded border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-2 py-1 text-left font-semibold">Number</th>
                <th className="px-2 py-1 text-right font-semibold">This deal</th>
                <th className="px-2 py-1 text-right font-semibold">Carrier's scope</th>
              </tr>
            </thead>
            <tbody>
              {off.map((f) => (
                <tr key={f.key} className="border-t border-slate-100">
                  <td className="px-2 py-1 text-slate-700">{f.label}</td>
                  <td className="px-2 py-1 text-right font-semibold text-rose-800">{f.key === "pwi_code" && !f.deal ? "none" : money(f.deal)}</td>
                  <td className="px-2 py-1 text-right text-slate-700">{f.key === "pwi_code" && !f.carrier ? "none" : money(f.carrier)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status.files.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-600">Open the scope:</span>
          {status.files.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => { openScopeFile(f.storage_path).catch((e) => setFileErr((e as Error).message)); }}
              className="text-bardin-teal hover:underline"
            >
              {f.file_name || "scope file"}
            </button>
          ))}
        </div>
      )}
      {fileErr && <div className="mt-1 text-[11px] text-rose-700">{fileErr}</div>}

      {showPicks && picks.length > 0 && (
        <div className="mt-3 rounded-lg bg-white border border-slate-200 p-2.5">
          <div className="mb-1 text-[11px] font-semibold text-slate-600">Left with the homeowner</div>
          <PickedLinesList picks={picks} />
        </div>
      )}

      {blocks && (
        <div className="mt-3 space-y-2">
          {numbersFix && (
            <button
              type="button"
              onClick={() => void onApproveWithCarrierNumbers()}
              disabled={busy}
              className="w-full sm:w-auto px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? "Approving…" : "Use the carrier's numbers and approve"}
            </button>
          )}
          {canOverride && !overridable ? (
            <div className="text-xs text-slate-600">
              {status.state === "reading"
                ? "The carrier's scope is still being read. Open this again in a minute; until then it can't be overridden."
                : `There's no carrier scope on this deal to check it against, so it can't be overridden. Send it back so ${rmFirst} uploads the scope.`}
            </div>
          ) : canOverride ? (
            <div className="rounded-lg bg-white border border-slate-200 p-2.5">
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Override: why are this deal's numbers right?</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. The carrier's revised estimate is on the way; the adjuster confirmed the new RCV by phone."
                className="w-full text-sm p-2 border border-slate-200 rounded bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rose-300"
              />
              <button
                type="button"
                onClick={() => void onOverride(reason.trim())}
                disabled={busy || !reasonOk}
                title={reasonOk ? undefined : "Say why in a few words"}
                className="mt-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {busy ? "Approving…" : "Override and approve"}
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-600">Only Alex or Chad can override the scope check.</div>
          )}
          <button
            type="button"
            onClick={() => onSendBack(sendBackNote)}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Send back to {rmFirst}
          </button>
        </div>
      )}
    </div>
  );
}
